import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Minus, Loader2, AlertCircle, Sparkles, CalendarClock } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";
import { MarkdownLite } from "@/components/MarkdownLite";
import { SourceBadge } from "@/components/SourceBadge";
import { nextFomc } from "@/lib/fomc";

const pct = (bps) => (bps == null ? "—" : `${(bps / 100).toFixed(2)}%`);

function FomcCountdown({ t, loc, en }) {
  const target = nextFomc();
  const targetMs = target ? target.getTime() : 0;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  if (!target) return null;

  const ms = Math.max(0, target.getTime() - now);
  const s = Math.floor(ms / 1000);
  const parts = [
    [en ? "D" : "J", Math.floor(s / 86400)],
    ["H", Math.floor((s % 86400) / 3600)],
    ["M", Math.floor((s % 3600) / 60)],
    ["S", s % 60],
  ];
  const dateStr = target.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long", timeZone: "America/New_York" });

  return (
    <div className="mb-4 rounded-lg border border-blue-900/40 bg-blue-500/5 px-3 py-2.5" data-testid="fomc-countdown">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-300">
        <CalendarClock className="h-3.5 w-3.5" /> {t("ir.fomcNext")}
      </div>
      <div className="mt-1 text-xs capitalize text-slate-300">{dateStr} · {t("ir.at2pm")}</div>
      <div className="mt-2 flex gap-2">
        {parts.map(([l, v]) => (
          <div key={l} className="flex min-w-[42px] flex-col items-center rounded-md bg-[#11141A] px-2 py-1" data-testid={`fomc-cd-${l}`}>
            <span className="font-mono text-lg font-bold text-white">{String(v).padStart(2, "0")}</span>
            <span className="text-[9px] uppercase text-slate-500">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Repricing({ label, now, past }) {
  if (now == null || past == null) return null;
  const diff = now - past;
  const up = diff > 0.5, down = diff < -0.5;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const color = up ? "#EF4444" : down ? "#10B981" : "#64748B";
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-slate-500">{label}</span>
      <Icon className="h-3.5 w-3.5" style={{ color }} />
      <span className="font-mono" style={{ color }}>{diff > 0 ? "+" : ""}{diff.toFixed(1)} bps</span>
    </div>
  );
}

export function InterestRates({ compact = false }) {
  const { t, lang } = useLang();
  const loc = lang === "en" ? "en-US" : "fr-FR";
  const bank = "fed";
  const rates = useNovexData(`/novex/interest-rates?bank=${bank}`);
  const ai = useNovexData(`/ai/rate-scenarios?bank=${bank}&lang=${lang}`, { enabled: !compact, maxPolls: 20 });

  const m = Array.isArray(rates.data?.meetings) && rates.data.meetings.length ? rates.data.meetings[0] : null;
  const cut = m?.cut_probability ?? 0, hold = m?.hold_probability ?? 0, hike = m?.hike_probability ?? 0;
  const baseLabel = { cut: t("ir.cutExp"), hold: t("ir.holdExp"), hike: t("ir.hikeExp") }[m?.base_case] || "—";
  const baseColor = { cut: "#10B981", hold: "#64748B", hike: "#EF4444" }[m?.base_case] || "#64748B";

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <span className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white" data-testid="bank-fed">🇺🇸 {t("ir.fedName")}</span>
        <SourceBadge source={rates.source} />
      </div>

      <FomcCountdown t={t} loc={loc} en={lang === "en"} />

      {rates.status === "loading" ? (
        <div className="flex items-center gap-2 py-6 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("ir.loadingExp")}</div>
      ) : rates.error ? (
        <div className="flex items-center gap-2 text-sm text-amber-500/80"><AlertCircle className="h-4 w-4" /> {rates.error}</div>
      ) : !m ? (
        <p className="text-sm text-slate-500">{t("ir.noBank")}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t("ir.nextMeeting")} · {m.meeting_label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-mono text-3xl font-bold text-white">{pct(m.expected_rate_bps)}</span>
                <span className="text-xs text-slate-500">{t("ir.expected", { c: pct(m.current_target_mid_bps) })}</span>
              </div>
            </div>
            <div className="rounded-md px-3 py-1.5 text-sm font-semibold" style={{ background: `${baseColor}1a`, color: baseColor }} data-testid="rate-base-case">
              {baseLabel}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-4">
            <Repricing label={t("ir.vs1w")} now={m.expected_rate_bps} past={m.expected_rate_one_week_ago_bps} />
            <Repricing label={t("ir.vs1m")} now={m.expected_rate_bps} past={m.expected_rate_one_month_ago_bps} />
          </div>

          {m.hold_probability == null ? (
            <p className="mt-4 rounded-md border border-amber-900/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-500/80">
              {t("ir.noProbs")}
            </p>
          ) : (
            <div className="mt-4">
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-[#222733]">
                <div style={{ width: `${cut}%`, background: "#10B981" }} title={`${t("ir.cut")} ${cut}%`} />
                <div style={{ width: `${hold}%`, background: "#64748B" }} title={`${t("ir.hold")} ${hold}%`} />
                <div style={{ width: `${hike}%`, background: "#EF4444" }} title={`${t("ir.hike")} ${hike}%`} />
              </div>
              <div className="mt-2 flex justify-between font-mono text-xs">
                <span className="text-[#10B981]">{t("ir.cut")} {Math.round(cut)}%</span>
                <span className="text-slate-400">{t("ir.hold")} {Math.round(hold)}%</span>
                <span className="text-[#EF4444]">{t("ir.hike")} {Math.round(hike)}%</span>
              </div>
              {rates.data?.implied_source && (
                <p className="mt-2 text-[10px] text-emerald-300/70">{t("ir.impliedMkt", { src: rates.data.implied_source })}</p>
              )}
            </div>
          )}

          {!compact && (
            <div className="mt-5 border-t border-[#222733] pt-4">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
                <Sparkles className="h-3.5 w-3.5" /> {t("ir.aiTitle")}
              </div>
              {ai.status === "loading" ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("ir.genScenarios")}</div>
              ) : ai.error ? (
                <p className="text-sm text-amber-500/80">{ai.error}</p>
              ) : ai.data?.analysis ? (
                <div className="text-sm text-slate-300"><MarkdownLite text={ai.data.analysis} /></div>
              ) : (
                <p className="text-sm text-slate-500">{t("ir.analysisNone")}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
